import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarGroup,
  MessageBarTitle,
  shorthands,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

import { useSignInState } from './signInState';
import { formatDateDDMMYYYY } from '../Helpers/dateUtils';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const useStyles = makeStyles({
  group: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    ...shorthands.overflow('auto'),
  },
});

interface SkuExpiringBannerProps {
  className?: string;
}

interface BannerCopy {
  title: string;
  body: string;
}

/**
 * Soft, dismissible warning shown on every signed-in route (mounted from
 * AppLayout) when the workspace SKU is still active but will expire within
 * the next 7 days. Copy is picked from the SKU type:
 *
 *   - Trial:        "Trial ending soon" / "...ends on {date}...upgrade."
 *   - Paid (non-trial): "Subscription expiring soon" / "...expires on {date}...renew."
 *
 * Anything more severe (no SKU, admin-disabled, already expired) is handled
 * by the full-screen blocker rendered from <App /> via SignUpFlow, so this
 * banner intentionally never fires for SKUs that have already lapsed.
 */
export const SkuExpiringBanner: FC<SkuExpiringBannerProps> = ({ className }) => {
  const styles = useStyles();
  const { signInState } = useSignInState();
  const [dismissed, setDismissed] = useState(false);

  // Re-show the banner when the user switches customer/workspace so a prior
  // dismissal on a different workspace does not silently hide a new warning.
  useEffect(() => {
    setDismissed(false);
  }, [signInState.currentCustomerID, signInState.workspaceSubscription]);

  const copy = useMemo<BannerCopy | null>(() => {
    if (signInState.isWorkspaceSuperAdmin)
      return null;

    const sku = signInState.workspaceSubscription?.skuDetails;
    if (!sku || !sku.isActive)
      return null;

    const exp = new Date(sku.expirationDate);
    if (Number.isNaN(exp.getTime()))
      return null;

    const now = new Date();
    if (exp <= now)
      return null;
    if (exp.getTime() - now.getTime() >= SEVEN_DAYS_MS)
      return null;

    const formattedExpiry = formatDateDDMMYYYY(sku.expirationDate);
    return sku.isTrial
      ? {
        title: 'Trial ending soon',
        body: `Your Clarity trial ends on ${formattedExpiry}. Speak to your administrator to upgrade.`,
      }
      : {
        title: 'Subscription expiring soon',
        body: `Your Clarity subscription expires on ${formattedExpiry}. Speak to your administrator to renew.`,
      };
  }, [signInState.workspaceSubscription, signInState.isWorkspaceSuperAdmin]);

  if (!copy || dismissed)
    return null;

  return (
    <MessageBarGroup className={mergeClasses(styles.group, className)}>
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>{copy.title}</MessageBarTitle>
          {copy.body}
        </MessageBarBody>
        <MessageBarActions
          containerAction={
            <Button
              onClick={() => setDismissed(true)}
              aria-label="dismiss"
              appearance="transparent"
              icon={<DismissRegular />}
            />
          }
        />
      </MessageBar>
    </MessageBarGroup>
  );
};

export default SkuExpiringBanner;
